<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { getAllotmentNextCertificateNo } from '@/features/startTask/api/officerAppointmentApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { useSessionStore } from '@/shared/stores/sessionStore';
import {
  getCurrentDateByJurisdiction,
  useDateField,
  useFormatDateByUserFormat,
} from '@/shared/composables/dateUtils';

interface Props {
  entityId?: string;
  initialData?: any;
  readOnly?: boolean;
  documents?: Array<{ templateName?: string; docCode?: string }>;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
}

interface TransferRow {
  id: string;
  fromShareholder: string;
  fromShareholderName: string;
  fromCategoryCd: string;
  fromShareholderID: string;
  fromRepresentativeName: string;
  toShareholder: string;
  toShareholderName: string;
  toCategoryCd: string;
  toShareholderID: string;
  toRepresentativeName: string;
  currency: string;
  currencyName: string;
  shareType: string;
  shareTypeName: string;
  fromNumberOfShares: string;
  toNumberOfShares: string;
  totalConsiderationPaid: string;
  fromShareholderCertNo: string;
  toShareholderCertNo: string;
  transferorHasWitness: boolean;
  transferorWitnessName: string;
  transfereeHasWitness: boolean;
  transfereeWitnessName: string;
}

const props = withDefaults(defineProps<Props>(), {
  entityId: '',
  initialData: null,
  readOnly: false,
  documents: () => [],
  selectedPerson: null,
  activePersonContext: null,
});

const emit = defineEmits<{
  (e: 'request-person-lookup', context: Record<string, any>): void;
  (e: 'validity-change', valid: boolean): void;
}>();

const readOnly = computed(() => !!props.readOnly);
const sessionStore = useSessionStore();
const formRoot = ref<HTMLElement | null>(null);
const requiredErrorKeys = ref<Set<string>>(new Set());

const entityCd = ref('');
const entityName = ref('');
const entityRegNo = ref('');
const companyAddress = ref('');
const transactionDate = ref('');

const requestingDirectorCd = ref('');
const requestingDirectorName = ref('');
const requestingDirectorId = ref('');
const requestingDirectorEmail = ref('');

const transferRows = ref<TransferRow[]>([]);
const fromShareholderOptions = ref<Array<{ title: string; value: string; subtitle?: string; certiNo?: string }>>([]);
const nextCertBase = ref(1);
const certNosFromSavedData = ref(false);

const currencyOptions = ref<Array<{ title: string; value: string; modalTitle?: string; bigo3?: string }>>([]);
const classOfSharesOptions = ref<Array<{ title: string; value: string }>>([]);

const MAX_DOC_CHIPS = 4;
const dismissedSuggestions = ref<Set<string>>(new Set());
const suggestedToday = computed(() => getCurrentDateByJurisdiction());
const formatDateByUserFormat = useFormatDateByUserFormat();

function uid(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function suggestionKey(field: string, value: string): string {
  return `${field}:${value}`;
}
function isSuggestionDismissed(field: string, value: string): boolean {
  if (!value) return true;
  return dismissedSuggestions.value.has(suggestionKey(field, value));
}
function dismissSuggestion(field: string, value: string) {
  if (!value) return;
  const next = new Set(dismissedSuggestions.value);
  next.add(suggestionKey(field, value));
  dismissedSuggestions.value = next;
}
function shouldShowDateSuggestion(field: string, value: string): boolean {
  if (!value) return false;
  return !isSuggestionDismissed(field, value);
}
function applySuggestedDate(value: string) {
  if (!value) return;
  transactionDate.value = value;
}
function formatIsoForSession(iso: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  if (isNaN(d.getTime())) return iso;
  return formatDateByUserFormat(d);
}

function createTransferRow(): TransferRow {
  return {
    id: uid('tr'),
    fromShareholder: '',
    fromShareholderName: '',
    fromCategoryCd: '',
    fromShareholderID: '',
    fromRepresentativeName: '',
    toShareholder: '',
    toShareholderName: '',
    toCategoryCd: '',
    toShareholderID: '',
    toRepresentativeName: '',
    currency: '',
    currencyName: '',
    shareType: '',
    shareTypeName: '',
    fromNumberOfShares: '',
    toNumberOfShares: '',
    totalConsiderationPaid: '',
    fromShareholderCertNo: '',
    toShareholderCertNo: '',
    transferorHasWitness: false,
    transferorWitnessName: '',
    transfereeHasWitness: false,
    transfereeWitnessName: '',
  };
}

function bindModelDate(
  get: () => string,
  set: (v: string) => void,
  field: ReturnType<typeof useDateField>,
) {
  watch(get, (v) => {
    const s = trim(v);
    if (field.dateValue.value !== s) field.dateValue.value = s;
  }, { immediate: true });
  watch(field.dateValue, (v) => {
    const s = trim(v);
    if (trim(get()) !== s) set(s);
  });
}

const transactionDateField = useDateField();
bindModelDate(() => transactionDate.value, (v) => { transactionDate.value = v; }, transactionDateField);
const transactionDateMenu = transactionDateField.dateMenu;
const transactionDateLocalText = transactionDateField.localText;
const transactionDateOnBlur = transactionDateField.onDateTextBlur;
const transactionDatePickerValue = transactionDateField.datePickerValue;
const transactionDatePlaceholder = transactionDateField.datePlaceholder;
const transactionDatePickerProps = transactionDateField.datePickerProps;

const docBuckets = computed(() => {
  const out = {
    letter: [] as string[],
    directors: [] as string[],
    members: [] as string[],
    transferForm: [] as string[],
  };
  for (const d of props.documents || []) {
    const name = trim(d.templateName);
    const code = trim(d.docCode).toUpperCase();
    const label = name || code;
    if (!label) continue;
    if (code === 'PRS050' || /director\s*letter/i.test(name)) out.letter.push(label);
    if (code === 'PRS051' || (/director/i.test(name) && /resolution/i.test(name))) out.directors.push(label);
    if (code === 'PRS052' || (/(member|shareholder)/i.test(name) && /resolution/i.test(name))) out.members.push(label);
    if (code === 'PRS053' || code === 'PRS054' || /share\s*transfer\s*form/i.test(name)) out.transferForm.push(label);
  }
  return {
    letter: Array.from(new Set(out.letter)),
    directors: Array.from(new Set(out.directors)),
    members: Array.from(new Set(out.members)),
    transferForm: Array.from(new Set(out.transferForm)),
  };
});

const currencyOptionItems = computed(() =>
  currencyOptions.value
    .filter((option) => option.value && option.value !== 'ALL')
    .map((option) => ({
      title: option.modalTitle || option.title || option.value,
      value: option.value,
    })),
);

function getLabelByCode(code: string, options: Array<{ title: string; value: string }>): string {
  const c = trim(code);
  if (!c) return '';
  return options.find((o) => o.value === c)?.title || '';
}

function buildAddressFromEntity(e: Record<string, any>): string {
  const parts = [
    [e.blockNo, e.streetName].filter(Boolean).join(' '),
    e.buildingName,
    e.postalCode ? `SINGAPORE ${e.postalCode}` : '',
  ].filter(Boolean);
  return parts.join(', ');
}

async function loadCodeOptions() {
  try {
    const [curRes, classRes] = await Promise.all([
      baseCodeApi.getCodesByMainCode('CM', 'B08', undefined),
      baseCodeApi.getCodesByMainCode('CM', 'A20', undefined),
    ]);
    const curCodes = (curRes as any)?.codes || (curRes as any)?.data?.codes || [];
    const classCodes = (classRes as any)?.codes || (classRes as any)?.data?.codes || [];

    const curUnique = new Map<string, { title: string; value: string; modalTitle?: string; bigo3?: string }>();
    (Array.isArray(curCodes) ? curCodes : []).forEach((code: any) => {
      const total = trim(code.totalCode || code.TOTAL_CODE);
      if (!total || curUnique.has(total) || total.slice(-3) === '000') return;
      const codeName = trim(code.defaultCodeName || code.CODE_NAME || code.codeName);
      const bigo3 = trim(code.bigo3 || code.BIGO3);
      const listDisplay = bigo3 || codeName || total;
      curUnique.set(total, {
        title: listDisplay,
        value: total,
        modalTitle: bigo3 && codeName ? `${bigo3} (${codeName})` : listDisplay,
        bigo3,
      });
    });
    const allCurrencies = Array.from(curUnique.values());
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const jurisdictionCurrencyMap: Record<string, string> = {
      SG: 'SGD', MY: 'MYR', VN: 'VND', CN: 'CNY', HK: 'HKD',
      KR: 'KRW', JP: 'JPY', TH: 'THB', ID: 'IDR', PH: 'PHP',
      IN: 'INR', AU: 'AUD', GB: 'GBP', US: 'USD', TW: 'TWD',
    };
    const targetCurrencyCode = jurisdiction ? jurisdictionCurrencyMap[jurisdiction] : null;
    currencyOptions.value = allCurrencies.sort((a, b) => {
      const aBigo3 = (a.bigo3 || '').toUpperCase();
      const bBigo3 = (b.bigo3 || '').toUpperCase();
      if (targetCurrencyCode) {
        if (aBigo3 === targetCurrencyCode && bBigo3 !== targetCurrencyCode) return -1;
        if (aBigo3 !== targetCurrencyCode && bBigo3 === targetCurrencyCode) return 1;
      }
      if (aBigo3 === 'USD' && bBigo3 !== 'USD') return -1;
      if (aBigo3 !== 'USD' && bBigo3 === 'USD') return 1;
      return aBigo3.localeCompare(bBigo3);
    });

    const classUnique = new Map<string, { title: string; value: string }>();
    (Array.isArray(classCodes) ? classCodes : []).forEach((code: any) => {
      const total = trim(code.totalCode || code.TOTAL_CODE);
      const name = trim(code.codeName || code.CODE_NAME || code.defaultCodeName);
      if (!total || !name || classUnique.has(total) || total.slice(-3) === '000') return;
      classUnique.set(total, { title: name, value: total });
    });
    classOfSharesOptions.value = Array.from(classUnique.values());
  } catch (e) {
    console.warn('[TransferPresetForm] code options load failed:', e);
  }
}

async function loadEntityMeta(cd: string) {
  try {
    const res = await fetch(`/api/startTask/common/entity/${encodeURIComponent(cd)}`, { credentials: 'include' });
    const json: any = await res.json();
    const e = json?.data?.entity || json?.data || json?.entity || null;
    if (!e) return;
    entityName.value = e.companyName || e.entityName || entityName.value;
    entityRegNo.value = e.registrationNo || e.regNo || entityRegNo.value;
    const address = buildAddressFromEntity(e);
    if (address) companyAddress.value = address;
  } catch (e) {
    console.warn('[TransferPresetForm] entity meta load failed:', e);
  }
}

async function loadFromShareholders(cd: string) {
  if (!cd) {
    fromShareholderOptions.value = [];
    return;
  }
  try {
    const res = await fetch(`/api/startTask/shares-transfer/shareholders/${encodeURIComponent(cd)}`, {
      credentials: 'include',
    });
    const json: any = await res.json();
    const list = json?.data?.shareholders || json?.shareholders || [];
    fromShareholderOptions.value = (Array.isArray(list) ? list : []).map((s: any) => {
      const shareData = s.shareData || {};
      return {
        title: trim(s.contactName || s.CONTACT_NAME || s.shareholderName || s.title),
        value: trim(s.contactCd || s.CONTACT_CD || s.value),
        subtitle: trim(s.contactRegNo || s.CONTACT_REG_NO),
        certiNo: trim(shareData.certiNo || s.certiNo || s.CERTI_NO),
      };
    }).filter((o) => o.title && o.value);
  } catch (e) {
    console.warn('[TransferPresetForm] shareholders load failed:', e);
    fromShareholderOptions.value = [];
  }
}

async function loadNextCertificateNo(cd: string) {
  try {
    const res: any = await getAllotmentNextCertificateNo(cd);
    const nextNo =
      res?.data?.data?.nextCertificateNo ||
      res?.data?.nextCertificateNo ||
      res?.nextCertificateNo ||
      '';
    const n = Number(trim(nextNo));
    nextCertBase.value = Number.isFinite(n) && n > 0 ? n : 1;
    assignCertNosFromBase({ force: !certNosFromSavedData.value });
  } catch (e) {
    console.warn('[TransferPresetForm] next certificate no load failed:', e);
    nextCertBase.value = 1;
    assignCertNosFromBase({ force: !certNosFromSavedData.value });
  }
}

function occupiedCertNumbers(): number[] {
  return transferRows.value
    .map((r) => Number(trim(r.toShareholderCertNo)))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function allocateNextCertNo(): string {
  const occupied = occupiedCertNumbers();
  const maxOccupied = occupied.length ? Math.max(...occupied) : 0;
  const next = Math.max(nextCertBase.value, maxOccupied + 1);
  return String(next);
}

/** Fill empty rows; when force=true (new task), resequence all from MAX+1. */
function assignCertNosFromBase(opts?: { force?: boolean }) {
  if (!transferRows.value.length) return;
  if (opts?.force) {
    let next = nextCertBase.value;
    for (const row of transferRows.value) {
      row.toShareholderCertNo = String(next);
      next += 1;
    }
    return;
  }
  let next = nextCertBase.value;
  const used = new Set(occupiedCertNumbers());
  for (const row of transferRows.value) {
    if (trim(row.toShareholderCertNo)) continue;
    while (used.has(next)) next += 1;
    row.toShareholderCertNo = String(next);
    used.add(next);
    next += 1;
  }
}

function applyContactToRow(row: TransferRow, contact: Record<string, any>, side: 'from' | 'to') {
  const name = trim(contact.contactName || contact.CONTACT_NAME || contact.name);
  const categoryCd = trim(contact.categoryCd || contact.CATEGORY_CD);
  const regNo = trim(contact.contactRegNo || contact.CONTACT_REG_NO || contact.regNo);
  const repName = trim(contact.representativeName || contact.REPRESENTATIVE_NAME);
  if (side === 'from') {
    if (name) row.fromShareholderName = name;
    if (categoryCd) row.fromCategoryCd = categoryCd;
    if (regNo) row.fromShareholderID = regNo;
    if (repName) row.fromRepresentativeName = repName;
  } else {
    if (name) row.toShareholderName = name;
    if (categoryCd) row.toCategoryCd = categoryCd;
    if (regNo) row.toShareholderID = regNo;
    if (repName) row.toRepresentativeName = repName;
  }
}

async function hydrateRowContact(row: TransferRow, contactCd: string, side: 'from' | 'to') {
  const cd = trim(contactCd);
  if (!cd) return;
  if (side === 'from') row.fromShareholder = cd;
  else row.toShareholder = cd;
  try {
    const res: any = await contactsApi.getContactById(cd, { entityCd: entityCd.value || undefined });
    const contact = (res as any)?.contact || (res as any)?.data?.contact || (res as any)?.data || null;
    if (contact && typeof contact === 'object') applyContactToRow(row, contact, side);
  } catch (e) {
    console.warn('[TransferPresetForm] contact load failed:', e);
  } finally {
    emitValidity();
  }
}

function onFromShareholderSelect(row: TransferRow, contactCd: string) {
  row.fromShareholder = trim(contactCd);
  const opt = fromShareholderOptions.value.find((o) => o.value === contactCd);
  if (opt) {
    row.fromShareholderName = opt.title;
    if (trim(opt.certiNo)) row.fromShareholderCertNo = trim(opt.certiNo);
  }
  void hydrateRowContact(row, contactCd, 'from');
}

function syncCurrencyName(row: TransferRow) {
  row.currencyName = getLabelByCode(row.currency, currencyOptions.value);
}
function syncClassName(row: TransferRow) {
  row.shareTypeName = getLabelByCode(row.shareType, classOfSharesOptions.value);
}
function onSharesChange(row: TransferRow) {
  const n = trim(row.fromNumberOfShares);
  row.toNumberOfShares = n;
}

function requestLookup(fieldGroup: string, row: TransferRow, rowIndex: number, defaultRole: string) {
  emit('request-person-lookup', {
    fieldGroup,
    defaultRole,
    rowId: row.id,
    rowIndex,
  });
}

function addTransferRow() {
  const row = createTransferRow();
  row.toShareholderCertNo = allocateNextCertNo();
  transferRows.value.push(row);
}
function removeTransferRow(idx: number) {
  if (transferRows.value.length <= 1) return;
  transferRows.value.splice(idx, 1);
}

function hasRequiredError(key: string): boolean {
  return requiredErrorKeys.value.has(key);
}

function firstMissingRequiredKeys(): string[] {
  const missing: string[] = [];
  const pushIfEmpty = (key: string, value: unknown) => {
    if (!trim(value)) missing.push(key);
  };
  pushIfEmpty('transactionDate', transactionDate.value);
  if (!transferRows.value.length) {
    missing.push('transfers.min');
    return missing;
  }
  transferRows.value.forEach((row, idx) => {
    pushIfEmpty(`transfers.${idx}.fromShareholder`, row.fromShareholder || row.fromShareholderName);
    pushIfEmpty(`transfers.${idx}.toShareholder`, row.toShareholder || row.toShareholderName);
    pushIfEmpty(`transfers.${idx}.currency`, row.currency);
    pushIfEmpty(`transfers.${idx}.shareType`, row.shareType);
    pushIfEmpty(`transfers.${idx}.fromNumberOfShares`, row.fromNumberOfShares);
    pushIfEmpty(`transfers.${idx}.totalConsiderationPaid`, row.totalConsiderationPaid);
    if (row.transferorHasWitness && !trim(row.transferorWitnessName)) {
      missing.push(`transfers.${idx}.transferorWitness`);
    }
    if (row.transfereeHasWitness && !trim(row.transfereeWitnessName)) {
      missing.push(`transfers.${idx}.transfereeWitness`);
    }
  });
  return missing;
}

async function focusRequiredField(key: string): Promise<void> {
  await nextTick();
  const root = formRoot.value;
  if (!root) return;
  const target = root.querySelector<HTMLElement>(`[data-required-key="${key}"]`);
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target?.querySelector<HTMLElement>('input, textarea, [role="combobox"]')?.focus();
}

async function validateAndFocusRequiredFields(): Promise<boolean> {
  if (!trim(entityCd.value)) return false;
  const missing = firstMissingRequiredKeys();
  requiredErrorKeys.value = new Set(missing);
  if (!missing.length) return true;
  await focusRequiredField(missing[0]);
  return false;
}

function emitValidity() {
  emit('validity-change', trim(entityCd.value) !== '' && firstMissingRequiredKeys().length === 0);
}

function toLegacyShareholders(): any[] {
  return transferRows.value.map((row) => ({
    id: row.id,
    fromShareholder: trim(row.fromShareholder),
    fromShareholderName: trim(row.fromShareholderName),
    fromCategoryCd: trim(row.fromCategoryCd),
    fromShareholderID: trim(row.fromShareholderID),
    fromRepresentativeName: trim(row.fromRepresentativeName),
    toShareholder: trim(row.toShareholder),
    toShareholderName: trim(row.toShareholderName),
    toCategoryCd: trim(row.toCategoryCd),
    toShareholderID: trim(row.toShareholderID),
    toRepresentativeName: trim(row.toRepresentativeName),
    fromNumberOfShares: trim(row.fromNumberOfShares),
    toNumberOfShares: trim(row.toNumberOfShares || row.fromNumberOfShares),
    totalConsiderationPaid: trim(row.totalConsiderationPaid),
    shareType: trim(row.shareType),
    shareTypeName: trim(row.shareTypeName),
    currency: trim(row.currency),
    currencyName: trim(row.currencyName),
    fromShareholderCertNo: trim(row.fromShareholderCertNo),
    toShareholderCertNo: trim(row.toShareholderCertNo),
    transferorHasWitness: !!row.transferorHasWitness,
    transferorWitnessName: trim(row.transferorWitnessName),
    transfereeHasWitness: !!row.transfereeHasWitness,
    transfereeWitnessName: trim(row.transfereeWitnessName),
  }));
}

function getFormData(): Record<string, any> {
  const shareholders = toLegacyShareholders();
  const first = shareholders[0] || {};
  return {
    entity: trim(entityCd.value),
    entityCd: trim(entityCd.value),
    entityName: trim(entityName.value),
    entityRegNo: trim(entityRegNo.value),
    companyAddress: trim(companyAddress.value),
    transactionDate: trim(transactionDate.value),
    documentDate: trim(transactionDate.value),
    executionDate: trim(transactionDate.value),
    requestingDirectorCd: trim(requestingDirectorCd.value),
    requestingDirectorName: trim(requestingDirectorName.value),
    requestingDirectorId: trim(requestingDirectorId.value),
    requestingDirectorEmail: trim(requestingDirectorEmail.value),
    shareholders,
    fromShareholder: first.fromShareholder,
    fromShareholderName: first.fromShareholderName,
    toShareholder: first.toShareholder,
    toShareholderName: first.toShareholderName,
    toNumberOfShares: first.toNumberOfShares,
    shareType: first.shareType,
  };
}

function transformCodeNameToValue(payload: any): any {
  if (Array.isArray(payload)) return payload.map(transformCodeNameToValue);
  if (!payload || typeof payload !== 'object') return payload;
  if ('code' in payload && 'name' in payload && Object.keys(payload).length <= 3) {
    return payload.name || payload.code || '';
  }
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(payload)) out[k] = transformCodeNameToValue(v);
  return out;
}

async function fetchContact(contactCd: string): Promise<Record<string, any> | null> {
  const cd = trim(contactCd);
  if (!cd) return null;
  try {
    const res: any = await contactsApi.getContactById(cd, { entityCd: entityCd.value || undefined });
    return (res as any)?.contact || (res as any)?.data?.contact || (res as any)?.data || null;
  } catch {
    return null;
  }
}

function contactField(contact: Record<string, any> | null | undefined, ...keys: string[]): string {
  if (!contact) return '';
  for (const key of keys) {
    const v = trim(contact[key]);
    if (v) return v;
  }
  return '';
}

function formatDateForEFiling(dateString: string): string {
  const raw = trim(dateString);
  if (!raw) return '';
  try {
    const datePart = raw.includes(' ') ? raw.split(' ')[0] : raw;
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const date = new Date(`${datePart}T00:00:00`);
      if (Number.isNaN(date.getTime())) return raw;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    }
    return raw;
  } catch {
    return raw;
  }
}

function formatDateToYYYYMMDD(dateString: string): string {
  const raw = trim(dateString);
  if (!raw) return '';
  try {
    const datePart = raw.includes(' ') ? raw.split(' ')[0] : raw;
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
    const date = new Date(datePart);
    if (Number.isNaN(date.getTime())) return raw;
    const y = String(date.getFullYear());
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  } catch {
    return raw;
  }
}

async function resolveCodeMeta(totalCode: string): Promise<{ codeName: string; bigo1: string; bigo3: string }> {
  const cd = trim(totalCode);
  if (!cd) return { codeName: '', bigo1: '', bigo3: '' };
  try {
    const response: any = await baseCodeApi.getCodeByTotalCode('CM', cd);
    const code = response?.code || response?.data?.code || {};
    return {
      codeName: trim(code.codeName || code.CODE_NAME),
      bigo1: trim(code.bigo1 || code.BIGO1),
      bigo3: trim(code.bigo3 || code.BIGO3),
    };
  } catch {
    return { codeName: '', bigo1: '', bigo3: '' };
  }
}

function getClassOfSharesAs(classOfSharesCd: string): string {
  const cd = trim(classOfSharesCd);
  if (!cd) return '';
  if (cd === 'A20001') return 'ORD';
  if (cd === 'A20002') return 'PREF';
  if (cd === 'A20003') return 'OTH';
  const title = (classOfSharesOptions.value.find((o) => o.value === cd)?.title || '').toLowerCase();
  if (title.includes('ordinary')) return 'ORD';
  if (title.includes('preference') || title.includes('pref')) return 'PREF';
  if (title.includes('other')) return 'OTH';
  return '';
}

function addressTypeChar(addressTypeCd: string): 'S' | 'F' {
  return trim(addressTypeCd) === 'B07002' ? 'F' : 'S';
}

/**
 * Classic SharesTransferForm.getEFilingData 스키마에 맞춤 (SG Transfer PreSet B32006).
 * validate는 IND/LC(/FC) + fromIssuedShareCapital 등을 요구함.
 */
async function getEFilingData() {
  const filingAgentUEN = sessionStore.getCustomerInfo()?.regNo || '';
  const shareholderList: any[] = [];

  for (const row of transferRows.value) {
    if (!trim(row.fromShareholder) || !trim(row.toShareholder)) continue;
    const fromContact = await fetchContact(row.fromShareholder);
    const toContact = await fetchContact(row.toShareholder);
    if (!fromContact || !toContact) continue;

    const fromCategoryCd =
      contactField(fromContact, 'CATEGORY_CD', 'categoryCd') || trim(row.fromCategoryCd);
    const toCategoryCd =
      contactField(toContact, 'CATEGORY_CD', 'categoryCd') || trim(row.toCategoryCd);
    // classic과 동일: B09001 = LC, 그 외 IND
    const fromCategoryType = fromCategoryCd === 'B09001' ? 'LC' : 'IND';
    const toCategoryType = toCategoryCd === 'B09001' ? 'LC' : 'IND';

    const fromIdTypeCd = contactField(fromContact, 'ID_TYPE_CD', 'idTypeCd');
    const toIdTypeCd = contactField(toContact, 'ID_TYPE_CD', 'idTypeCd');
    const fromNationalityCd = contactField(fromContact, 'NATIONALITY_CD', 'nationalityCd');
    const toNationalityCd = contactField(toContact, 'NATIONALITY_CD', 'nationalityCd');

    const [fromCategoryMeta, toCategoryMeta, fromIdTypeMeta, toIdTypeMeta, fromNatMeta, toNatMeta] =
      await Promise.all([
        resolveCodeMeta(fromCategoryCd),
        resolveCodeMeta(toCategoryCd),
        resolveCodeMeta(fromIdTypeCd),
        resolveCodeMeta(toIdTypeCd),
        resolveCodeMeta(fromNationalityCd),
        resolveCodeMeta(toNationalityCd),
      ]);

    const currencyValue = trim(row.currency);
    const currencyCdRaw = currencyValue.includes('_') ? currencyValue.split('_')[0] : currencyValue;
    let currencyCodeForCd = currencyCdRaw;
    let currencyName = trim(row.currencyName);
    let currencyAs = '';
    let currencyNameForApi = '';
    if (currencyCdRaw) {
      try {
        const allCurrencyResponse: any = await baseCodeApi.getCodesByMainCode('CM', 'B08', undefined);
        const allCurrencyCodes = allCurrencyResponse?.codes || allCurrencyResponse?.data?.codes || [];
        const matchingCode = (Array.isArray(allCurrencyCodes) ? allCurrencyCodes : []).find(
          (c: any) =>
            trim(c.bigo1 || c.BIGO1) === currencyCdRaw ||
            trim(c.totalCode || c.TOTAL_CODE) === currencyCdRaw ||
            trim(c.bigo3 || c.BIGO3).toUpperCase() === currencyCdRaw.toUpperCase(),
        );
        if (matchingCode) {
          currencyCodeForCd = trim(matchingCode.totalCode || matchingCode.TOTAL_CODE) || currencyCdRaw;
        }
        const meta = await resolveCodeMeta(currencyCodeForCd);
        currencyName = meta.bigo1 || currencyName;
        currencyAs = meta.bigo3 || '';
        currencyNameForApi = meta.codeName || '';
      } catch (e) {
        console.warn('[TransferPresetForm] Currency meta lookup failed:', e);
      }
    }

    const classOfSharesCd = trim(row.shareType);
    let classOfSharesName = trim(row.shareTypeName);
    if (!classOfSharesName && classOfSharesCd) {
      const classMeta = await resolveCodeMeta(classOfSharesCd);
      classOfSharesName = classMeta.codeName;
    }

    const toCountryCd = contactField(toContact, 'COUNTRY_CD', 'countryCd');
    const countryMeta = await resolveCodeMeta(toCountryCd);
    const mobileNoCountryCode = countryMeta.bigo3.replace(/^\+/, '');

    const fromAddressType = addressTypeChar(
      contactField(fromContact, 'ADDRESS_TYPE_CD', 'addressTypeCd') || 'B07001',
    );
    const toAddressType = addressTypeChar(
      contactField(toContact, 'ADDRESS_TYPE_CD', 'addressTypeCd') || 'B07001',
    );
    const toContactAddressType = addressTypeChar(
      contactField(toContact, 'ALTERNATE_ADDRESS_CD', 'alternateAddressCd', 'ADDRESS_TYPE_CD', 'addressTypeCd') ||
        'B07001',
    );

    const fromName =
      trim(row.fromShareholderName) || contactField(fromContact, 'CONTACT_NAME', 'contactName');
    const toName =
      trim(row.toShareholderName) || contactField(toContact, 'CONTACT_NAME', 'contactName');
    const fromUen =
      trim(row.fromShareholderID) || contactField(fromContact, 'CONTACT_REG_NO', 'contactRegNo');
    const toUen =
      trim(row.toShareholderID) || contactField(toContact, 'CONTACT_REG_NO', 'contactRegNo');

    const numberOfShares = trim(row.toNumberOfShares || row.fromNumberOfShares);
    const considerationPaid = trim(row.totalConsiderationPaid);
    // PreSet UI에 issued capital 입력란이 없어 consideration을 capital에도 사용해 validate 통과
    const capitalAmount = considerationPaid || '0';
    const transactionDateFormatted = formatDateForEFiling(transactionDate.value);

    const saveShareholderTransfer: Record<string, any> = {
      tranDetailIdx: 0,
      certiNoTo: trim(row.toShareholderCertNo),
      certiTypeTo: '',
      category: {
        code: toCategoryCd,
        name: toCategoryMeta.bigo3 || toCategoryMeta.codeName || toCategoryCd,
      },
      categoryType: toCategoryType,
      categoryCd: toCategoryCd,
      toUen,
      transferTo: trim(row.toShareholder),
      transferToName: toName,
      contactCd: trim(row.toShareholder),
      contactName: toName,
      shareholderName: toName,
      uen: toUen,
      ...(toCategoryType === 'IND'
        ? {
            nationality: {
              code: toNationalityCd,
              name: toNatMeta.bigo1 || toNatMeta.codeName || toNationalityCd,
            },
            idType: {
              code: toIdTypeCd,
              name: toIdTypeMeta.bigo1 || toIdTypeMeta.codeName || toIdTypeCd,
            },
            contactEmail: contactField(toContact, 'CONTACT_EMAIL', 'contactEmail', 'EMAIL'),
            countryCode: mobileNoCountryCode,
            mobileNo: contactField(toContact, 'MOBILE_NO', 'mobileNo'),
            birthDate: (contactField(toContact, 'DATE_OF_BIRTH', 'dateOfBirth').split(' ')[0] || ''),
            hasBirthDate: !!contactField(toContact, 'DATE_OF_BIRTH', 'dateOfBirth'),
          }
        : {}),
      addressType: toAddressType,
      postalCode: toAddressType === 'S' ? contactField(toContact, 'POSTAL_CODE', 'postalCode') : '',
      unitNo: toAddressType === 'S' ? contactField(toContact, 'ADDR_UNIT', 'addrUnit', 'unitNo') : '',
      levelNo: toAddressType === 'S' ? contactField(toContact, 'ADDR_LEVEL', 'addrLevel', 'levelNo') : '',
      blkhseNo: toAddressType === 'S' ? contactField(toContact, 'BLOCK_NO', 'blockNo') : '',
      streetName: toAddressType === 'S' ? contactField(toContact, 'STREET_NAME', 'streetName') : '',
      buildingName: toAddressType === 'S' ? contactField(toContact, 'BUILDING_NAME', 'buildingName') : '',
      foreignAddr1:
        toAddressType === 'F'
          ? contactField(toContact, 'FOREIGN_ADDRESS_LINE_1', 'foreignAddressLine1', 'foreignAddr1')
          : '',
      foreignAddr2:
        toAddressType === 'F'
          ? contactField(toContact, 'FOREIGN_ADDRESS_LINE_2', 'foreignAddressLine2', 'foreignAddr2')
          : '',
      contactAddressType: toContactAddressType,
      contactPostalCode:
        toContactAddressType === 'S' ? contactField(toContact, 'ALT_POSTAL_CODE', 'altPostalCode') : '',
      contactUnitNo:
        toContactAddressType === 'S' ? contactField(toContact, 'ALT_ADDR_UNIT', 'altAddrUnit') : '',
      contactLevelNo:
        toContactAddressType === 'S' ? contactField(toContact, 'ALT_ADDR_LEVEL', 'altAddrLevel') : '',
      contactBlkhseNo:
        toContactAddressType === 'S' ? contactField(toContact, 'ALT_BLOCK_NO', 'altBlockNo') : '',
      contactStreetName:
        toContactAddressType === 'S' ? contactField(toContact, 'ALT_STREET_NAME', 'altStreetName') : '',
      contactBuildingName:
        toContactAddressType === 'S'
          ? contactField(toContact, 'ALT_BUILDING_NAME', 'altBuildingName')
          : '',
      contactForeignAddr1:
        toContactAddressType === 'F'
          ? contactField(toContact, 'ALT_FOREIGN_ADDRESS_LINE_1', 'altForeignAddr1')
          : '',
      contactForeignAddr2:
        toContactAddressType === 'F'
          ? contactField(toContact, 'ALT_FOREIGN_ADDRESS_LINE_2', 'altForeignAddr2')
          : '',
      certiNoFrom: trim(row.fromShareholderCertNo),
      certiTypeFrom: '',
      fromCategory: {
        code: fromCategoryCd,
        name: fromCategoryMeta.bigo3 || fromCategoryMeta.codeName || fromCategoryCd,
      },
      fromCategoryType,
      fromUen,
      ...(fromCategoryType === 'IND'
        ? {
            fromIdType: {
              code: fromIdTypeCd,
              name: fromIdTypeMeta.bigo1 || fromIdTypeMeta.codeName || fromIdTypeCd,
            },
            fromNationality: {
              code: fromNationalityCd,
              name: fromNatMeta.bigo1 || fromNatMeta.codeName || fromNationalityCd,
            },
          }
        : {}),
      transferFrom: trim(row.fromShareholder),
      transferFromName: fromName,
      currency: {
        code: currencyCodeForCd || currencyCdRaw,
        name: currencyName || currencyCdRaw,
      },
      currencyCd: currencyCodeForCd || currencyCdRaw,
      currencyAs,
      currencyName: currencyNameForApi || currencyName,
      classOfShares: {
        code: classOfSharesCd,
        name: classOfSharesName || classOfSharesCd,
      },
      classOfSharesAs: getClassOfSharesAs(classOfSharesCd),
      transactionType: 'A18002',
      transactionName: 'Transfer',
      transactionDate: transactionDateFormatted,
      numberOfShares,
      issuedShareCapital: capitalAmount,
      paidUpShareCapital: capitalAmount,
      considerationPaid,
      fromNumberOfShares: trim(row.fromNumberOfShares),
      fromIssuedShareCapital: capitalAmount,
      fromPaidUpShareCapital: capitalAmount,
      oldnumberOfShares: '',
      oldpaidUpShareCapital: '',
      shareholderProfileIdFrom: contactField(fromContact, 'PROFILE_ID', 'profileId'),
      shareholderProfileId: contactField(toContact, 'PROFILE_ID', 'profileId'),
      effectiveDate: formatDateToYYYYMMDD(transactionDate.value),
      entityUen: trim(entityRegNo.value),
      fromAddressType,
    };

    shareholderList.push(saveShareholderTransfer);
  }

  const eFilingDataValidation = {
    ShareholderList: shareholderList,
    EntityUEN: trim(entityRegNo.value),
    FilingAgentUEN: trim(filingAgentUEN),
    entityName: trim(entityName.value),
  };
  return {
    eFilingDataValidation,
    eFilingDataForApi: transformCodeNameToValue(eFilingDataValidation),
  };
}

function setFormData(data: any) {
  if (!data || typeof data !== 'object') return;
  entityCd.value = trim(data.entityCd || data.entity || entityCd.value);
  entityName.value = trim(data.entityName || data.companyName || entityName.value);
  entityRegNo.value = trim(data.entityRegNo || data.companyRegistrationNo || entityRegNo.value);
  companyAddress.value = trim(data.companyAddress || companyAddress.value);
  transactionDate.value = trim(data.transactionDate || transactionDate.value);
  requestingDirectorCd.value = trim(data.requestingDirectorCd || '');
  requestingDirectorName.value = trim(data.requestingDirectorName || '');
  requestingDirectorId.value = trim(data.requestingDirectorId || '');
  requestingDirectorEmail.value = trim(data.requestingDirectorEmail || '');

  const rows = Array.isArray(data.shareholders) ? data.shareholders : [];
  if (rows.length > 0) {
    transferRows.value = rows.map((r: any) => ({
      ...createTransferRow(),
      id: trim(r.id) || uid('tr'),
      fromShareholder: trim(r.fromShareholder),
      fromShareholderName: trim(r.fromShareholderName),
      fromCategoryCd: trim(r.fromCategoryCd),
      fromShareholderID: trim(r.fromShareholderID),
      fromRepresentativeName: trim(r.fromRepresentativeName),
      toShareholder: trim(r.toShareholder),
      toShareholderName: trim(r.toShareholderName),
      toCategoryCd: trim(r.toCategoryCd),
      toShareholderID: trim(r.toShareholderID),
      toRepresentativeName: trim(r.toRepresentativeName),
      currency: trim(r.currency),
      currencyName: trim(r.currencyName),
      shareType: trim(r.shareType),
      shareTypeName: trim(r.shareTypeName),
      fromNumberOfShares: trim(r.fromNumberOfShares),
      toNumberOfShares: trim(r.toNumberOfShares),
      totalConsiderationPaid: trim(r.totalConsiderationPaid),
      fromShareholderCertNo: trim(r.fromShareholderCertNo || r.fromShareCertificateNo),
      toShareholderCertNo: trim(r.toShareholderCertNo || r.toShareCertificateNo),
      transferorHasWitness: !!r.transferorHasWitness,
      transferorWitnessName: trim(r.transferorWitnessName),
      transfereeHasWitness: !!r.transfereeHasWitness,
      transfereeWitnessName: trim(r.transfereeWitnessName),
    }));
    certNosFromSavedData.value = transferRows.value.some((r) => !!trim(r.toShareholderCertNo));
  }
}

watch(() => props.entityId, async (newEntityId) => {
  const cd = trim(newEntityId);
  if (!cd) return;
  entityCd.value = cd;
  await Promise.all([
    loadEntityMeta(cd),
    loadFromShareholders(cd),
    loadCodeOptions(),
    loadNextCertificateNo(cd),
  ]);
}, { immediate: true });

watch(() => props.initialData, (data) => {
  if (data && typeof data === 'object') setFormData(data.formData || data);
}, { immediate: true, deep: true });

watch(() => props.selectedPerson, (person) => {
  if (!person) return;
  const ctx = props.activePersonContext;
  if (!ctx) return;
  const fg = trim(ctx.fieldGroup);
  const rowId = trim((ctx as any).rowId);
  const rowIndex = Number((ctx as any).rowIndex);
  const targetRow =
    transferRows.value.find((r) => r.id === rowId) ||
    (Number.isInteger(rowIndex) && rowIndex >= 0 ? transferRows.value[rowIndex] : null);

  const raw = person as any;
  const contactCd = trim(raw.contactCd || raw.CONTACT_CD || '');
  const contactName = trim(raw.name || raw.contactName || raw.CONTACT_NAME || '');

  if (fg === 'requestingDirector') {
    if (contactCd) requestingDirectorCd.value = contactCd;
    if (contactName) requestingDirectorName.value = contactName;
    requestingDirectorId.value = trim(raw.contactRegNo || raw.CONTACT_REG_NO || raw.contactIdUen || '');
    requestingDirectorEmail.value = trim(raw.contactEmail || raw.email || '');
    emitValidity();
    return;
  }

  if (!targetRow) return;
  if (fg === 'transferor') {
    if (contactCd) {
      const opt = fromShareholderOptions.value.find((o) => o.value === contactCd);
      if (opt && trim(opt.certiNo)) targetRow.fromShareholderCertNo = trim(opt.certiNo);
      void hydrateRowContact(targetRow, contactCd, 'from');
    } else if (contactName) {
      targetRow.fromShareholderName = contactName;
    }
    applyContactToRow(targetRow, raw, 'from');
  } else if (fg === 'transferee') {
    if (contactCd) void hydrateRowContact(targetRow, contactCd, 'to');
    else if (contactName) targetRow.toShareholderName = contactName;
    applyContactToRow(targetRow, raw, 'to');
  } else if (fg === 'transferorWitness') {
    if (contactName) targetRow.transferorWitnessName = contactName;
  } else if (fg === 'transfereeWitness') {
    if (contactName) targetRow.transfereeWitnessName = contactName;
  }
  emitValidity();
});

watch([transferRows, transactionDate, entityCd], () => {
  emitValidity();
  if (requiredErrorKeys.value.size === 0) return;
  requiredErrorKeys.value = new Set(firstMissingRequiredKeys());
}, { deep: true, immediate: true });

onMounted(async () => {
  if (!transferRows.value.length) {
    transferRows.value = [createTransferRow()];
  }
  // Only fill empty rows. Do not force-stamp here — nextCertBase may still be
  // the default 1 before MAX+1 API returns.
  assignCertNosFromBase({ force: false });
  await loadCodeOptions();
  emitValidity();
});

defineExpose({
  setFormData,
  getFormData,
  getEFilingData,
  validateAndFocusRequiredFields,
});
</script>

<template>
  <div ref="formRoot" class="tsf-wrap">
    <div class="tsf-section">
      <div class="tsf-header">
        <Icon icon="mdi:domain" width="16" color="#666" />
        <span>Company</span>
      </div>
      <v-row dense class="pa-3">
        <v-col cols="12" md="6">
          <v-text-field v-model="entityName" label="Company name" density="compact" variant="outlined" readonly />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="entityRegNo" label="UEN / Reg. No." density="compact" variant="outlined" readonly />
        </v-col>
        <v-col cols="12">
          <v-text-field v-model="companyAddress" label="Registered office address" density="compact" variant="outlined" readonly />
        </v-col>
      </v-row>
    </div>

    <div class="tsf-section">
      <div class="tsf-header">
        <Icon icon="mdi:calendar" width="16" color="#666" />
        <span>Transaction</span>
      </div>
      <v-row dense class="pa-3">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="transactionDateLocalText"
            label="Transaction date *"
            density="compact"
            variant="outlined"
            :readonly="!!readOnly"
            :error="hasRequiredError('transactionDate')"
            data-required-key="transactionDate"
            @blur="transactionDateOnBlur"
          >
            <template v-if="!readOnly" #append-inner>
              <v-menu v-model="transactionDateMenu" :close-on-content-click="false">
                <template #activator="{ props: mProps }">
                  <v-icon v-bind="mProps" size="small">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="transactionDatePickerProps"
                  v-model="transactionDatePickerValue"
                  @update:model-value="transactionDateMenu = false"
                />
              </v-menu>
            </template>
          </v-text-field>
          <v-chip
            v-if="shouldShowDateSuggestion('transactionDate', suggestedToday)"
            size="x-small"
            variant="outlined"
            color="warning"
            prepend-icon="mdi-lightbulb-outline"
            closable
            close-icon="mdi-close"
            class="mt-1 tsf-suggest-chip"
            @mousedown.stop.prevent
            @click.stop.prevent="applySuggestedDate(suggestedToday)"
            @click:close.stop.prevent="dismissSuggestion('transactionDate', suggestedToday)"
          >
            Today: {{ formatIsoForSession(suggestedToday) }}
          </v-chip>
        </v-col>
      </v-row>
    </div>

    <div class="tsf-section">
      <div class="tsf-header">
        <Icon icon="mdi:account-tie" width="16" color="#666" />
        <span>Director Letter</span>
        <div class="tsf-docs">
          <v-chip v-for="(n, i) in docBuckets.letter.slice(0, MAX_DOC_CHIPS)" :key="'dl-' + i" size="x-small" color="primary" variant="tonal">{{ n }}</v-chip>
        </div>
      </div>
      <v-row dense class="pa-3">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="requestingDirectorName"
            label="Requesting director"
            density="compact"
            variant="outlined"
            :readonly="!!readOnly"
          >
            <template v-if="!readOnly" #append-inner>
              <v-btn
                icon="mdi-creation"
                size="x-small"
                variant="text"
                color="primary"
                @click.stop="requestLookup('requestingDirector', transferRows[0], 0, 'DIRECTOR')"
              />
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="requestingDirectorId" label="Director ID" density="compact" variant="outlined" :readonly="!!readOnly" />
        </v-col>
      </v-row>
    </div>

    <div class="tsf-section">
      <div class="tsf-header">
        <Icon icon="mdi:swap-horizontal" width="16" color="#666" />
        <span>Transfers</span>
        <v-spacer />
        <v-btn size="small" variant="tonal" color="primary" :disabled="!!readOnly" @click="addTransferRow">Add Transfer</v-btn>
      </div>
      <div class="pa-3 tsf-stack">
        <div v-for="(row, idx) in transferRows" :key="row.id" class="tsf-card">
          <div class="tsf-card-top">
            <strong>Transfer {{ idx + 1 }}</strong>
            <v-btn size="x-small" variant="text" color="error" :disabled="!!readOnly || transferRows.length <= 1" @click="removeTransferRow(idx)">Remove</v-btn>
          </div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-autocomplete
                :model-value="row.fromShareholder"
                :items="fromShareholderOptions"
                item-title="title"
                item-value="value"
                label="Transferor (shareholder) *"
                density="compact"
                variant="outlined"
                :readonly="!!readOnly"
                :error="hasRequiredError(`transfers.${idx}.fromShareholder`)"
                :data-required-key="`transfers.${idx}.fromShareholder`"
                @update:model-value="onFromShareholderSelect(row, $event)"
              >
                <template v-if="!readOnly" #append-inner>
                  <v-btn icon="mdi-creation" size="x-small" variant="text" color="primary" @click.stop="requestLookup('transferor', row, idx, 'SHAREHOLDER')" />
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="row.toShareholderName"
                label="Transferee *"
                density="compact"
                variant="outlined"
                :readonly="!!readOnly"
                :error="hasRequiredError(`transfers.${idx}.toShareholder`)"
                :data-required-key="`transfers.${idx}.toShareholder`"
              >
                <template v-if="!readOnly" #append-inner>
                  <v-btn icon="mdi-creation" size="x-small" variant="text" color="primary" @click.stop="requestLookup('transferee', row, idx, 'CONTACT')" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="row.currency"
                :items="currencyOptionItems"
                item-title="title"
                item-value="value"
                label="Currency *"
                placeholder="Please Select"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :readonly="!!readOnly"
                :error="hasRequiredError(`transfers.${idx}.currency`)"
                :data-required-key="`transfers.${idx}.currency`"
                clearable
                @update:model-value="syncCurrencyName(row)"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="row.shareType"
                :items="classOfSharesOptions"
                item-title="title"
                item-value="value"
                label="Class of shares *"
                density="compact"
                variant="outlined"
                :readonly="!!readOnly"
                :error="hasRequiredError(`transfers.${idx}.shareType`)"
                :data-required-key="`transfers.${idx}.shareType`"
                @update:model-value="syncClassName(row)"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="row.fromNumberOfShares"
                label="Number of shares *"
                density="compact"
                variant="outlined"
                :readonly="!!readOnly"
                :error="hasRequiredError(`transfers.${idx}.fromNumberOfShares`)"
                :data-required-key="`transfers.${idx}.fromNumberOfShares`"
                @update:model-value="onSharesChange(row)"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="row.totalConsiderationPaid"
                label="Consideration *"
                density="compact"
                variant="outlined"
                :readonly="!!readOnly"
                :error="hasRequiredError(`transfers.${idx}.totalConsiderationPaid`)"
                :data-required-key="`transfers.${idx}.totalConsiderationPaid`"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="row.toShareholderCertNo"
                label="Certificate No"
                density="compact"
                variant="outlined"
                :readonly="!!readOnly"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-checkbox v-model="row.transferorHasWitness" label="Transferor has witness" density="compact" hide-details :disabled="!!readOnly" />
              <v-text-field
                v-if="row.transferorHasWitness"
                v-model="row.transferorWitnessName"
                label="Transferor witness name"
                density="compact"
                variant="outlined"
                class="mt-1"
                :readonly="!!readOnly"
                :error="hasRequiredError(`transfers.${idx}.transferorWitness`)"
                :data-required-key="`transfers.${idx}.transferorWitness`"
              >
                <template v-if="!readOnly" #append-inner>
                  <v-btn
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    @click.stop="requestLookup('transferorWitness', row, idx, 'CONTACT')"
                  />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-checkbox v-model="row.transfereeHasWitness" label="Transferee has witness" density="compact" hide-details :disabled="!!readOnly" />
              <v-text-field
                v-if="row.transfereeHasWitness"
                v-model="row.transfereeWitnessName"
                label="Transferee witness name"
                density="compact"
                variant="outlined"
                class="mt-1"
                :readonly="!!readOnly"
                :error="hasRequiredError(`transfers.${idx}.transfereeWitness`)"
                :data-required-key="`transfers.${idx}.transfereeWitness`"
              >
                <template v-if="!readOnly" #append-inner>
                  <v-btn
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    @click.stop="requestLookup('transfereeWitness', row, idx, 'CONTACT')"
                  />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tsf-wrap { display: flex; flex-direction: column; gap: 12px; }
.tsf-section { background: #fff; border: 1px solid #e9edf5; border-radius: 12px; overflow: hidden; }
.tsf-header { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #283593; padding: 10px 14px; background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%); border-bottom: 1px solid #d6dcf2; flex-wrap: wrap; }
.tsf-docs { display: flex; flex-wrap: wrap; gap: 4px; margin-left: auto; }
.tsf-stack { display: flex; flex-direction: column; gap: 12px; }
.tsf-card { border: 1px solid #eee; border-radius: 8px; padding: 12px; background: #fafbff; }
.tsf-card-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.tsf-suggest-chip { cursor: pointer; }
</style>
